import idbService from '../idb';

// Stored in IDB so the service worker (which has no localStorage access) can
// read the same value that the main thread reads. Cross-tab sync is handled
// via BroadcastChannel; the service worker is notified via postMessage.

const READY_STORE = 'ready';
const OFFLINE_MODE_KEY = 'offline_mode';
const CHANNEL_NAME = 'post2-offline-mode';
const SW_MESSAGE_TYPE = 'OFFLINE_MODE_CHANGED';
const SW_ACK_TYPE = 'OFFLINE_MODE_ACK';
// Cap on how long we'll wait for the SW to acknowledge the offline-mode
// change. 500ms is well above the ~1ms a healthy SW needs but short enough
// that a wedged SW doesn't hang the UI. If we time out, the flag-write in
// IDB has already happened, so the SW will sync itself on its next activate.
const SW_ACK_TIMEOUT_MS = 500;

let cachedValue = false;
let channel = null;
const listeners = new Set();

const notifyLocal = (value) => {
  listeners.forEach((cb) => {
    try {
      cb(value);
    } catch (e) {
      // listener errors shouldn't break the chain
    }
  });
};

const getChannel = () => {
  if (typeof BroadcastChannel === 'undefined') return null;
  if (!channel) channel = new BroadcastChannel(CHANNEL_NAME);
  return channel;
};

const readFromIDB = async () => {
  try {
    const db = await idbService.initiateIDB();
    const value = await db.transaction(READY_STORE).objectStore(READY_STORE).get(OFFLINE_MODE_KEY);
    return value === true;
  } catch (e) {
    return false;
  }
};

const writeToIDB = async (value) => {
  const db = await idbService.initiateIDB();
  await db
    .transaction(READY_STORE, 'readwrite')
    .objectStore(READY_STORE)
    .put(value === true, OFFLINE_MODE_KEY);
};

// Post the offline-mode change to the SW and RESOLVE ONLY AFTER the SW
// acknowledges. Callers must await this to avoid a race where a fetch fires
// on the main thread before the SW has processed the message — the SW would
// still see the old cachedOfflineMode and serve stale IDB data even though
// the user just went online/offline. Falls back to an unacknowledged resolve
// after SW_ACK_TIMEOUT_MS so a stuck SW can't hang the UI.
const notifyServiceWorker = (value) =>
  new Promise((resolve) => {
    if (typeof navigator === 'undefined' || !navigator.serviceWorker?.controller) {
      resolve();
      return;
    }
    let ackChannel;
    try {
      ackChannel = new MessageChannel();
    } catch (e) {
      resolve();
      return;
    }
    const timer = setTimeout(() => {
      ackChannel.port1.onmessage = null;
      resolve();
    }, SW_ACK_TIMEOUT_MS);
    ackChannel.port1.onmessage = (event) => {
      if (event.data?.type !== SW_ACK_TYPE) return;
      clearTimeout(timer);
      ackChannel.port1.onmessage = null;
      resolve();
    };
    try {
      navigator.serviceWorker.controller.postMessage({ type: SW_MESSAGE_TYPE, value }, [ackChannel.port2]);
    } catch (e) {
      clearTimeout(timer);
      ackChannel.port1.onmessage = null;
      resolve();
    }
  });

const get = async () => {
  const value = await readFromIDB();
  cachedValue = value;
  return value;
};

const getCached = () => cachedValue;

const set = async (value) => {
  const next = value === true;
  await writeToIDB(next);
  cachedValue = next;
  // CRITICAL ORDERING: tell the SW and wait for its ack BEFORE firing any
  // same-tab subscribers or cross-tab broadcasts. notifyLocal synchronously
  // flips useOfflineMode, which re-renders components and lets them
  // dispatch /assignments etc. within the same microtask — if the SW
  // hasn't flipped yet, those requests hit the stale cachedOfflineMode in
  // the SW and get served from IDB (the "1 corrupted row after Go Online"
  // bug). Broadcasting to other tabs before the ack has the same problem
  // across windows, since every tab shares the one SW.
  await notifyServiceWorker(next);
  // Fire same-tab subscribers — BroadcastChannel.postMessage does NOT fire
  // the message event in the posting tab, only in other tabs/windows.
  notifyLocal(next);
  const ch = getChannel();
  if (ch) {
    try {
      ch.postMessage({ type: SW_MESSAGE_TYPE, value: next });
    } catch (e) {
      // ignore: BroadcastChannel may be closed
    }
  }
  return next;
};

const subscribe = (callback) => {
  listeners.add(callback);
  const ch = getChannel();
  let chHandler = null;
  if (ch) {
    chHandler = (event) => {
      if (event.data?.type === SW_MESSAGE_TYPE) {
        cachedValue = event.data.value === true;
        callback(cachedValue);
      }
    };
    ch.addEventListener('message', chHandler);
  }
  return () => {
    listeners.delete(callback);
    if (ch && chHandler) ch.removeEventListener('message', chHandler);
  };
};

// Sync the current IDB value into the SW once on app boot (covers the case
// where the SW was terminated and woke up after the main thread already had
// the offline mode set).
const syncToServiceWorker = async () => {
  const value = await readFromIDB();
  cachedValue = value;
  if (typeof navigator !== 'undefined' && navigator.serviceWorker) {
    try {
      const registration = await navigator.serviceWorker.ready;
      const target = registration.active || navigator.serviceWorker.controller;
      if (target) target.postMessage({ type: SW_MESSAGE_TYPE, value });
    } catch (e) {
      // SW not registered or not ready — SW will read IDB on its own on activate
    }
  }
};

// Kick off a best-effort cache populate on import so getCached() is usable
// shortly after page load.
if (typeof window !== 'undefined') {
  readFromIDB().then((value) => {
    cachedValue = value;
  });
  const ch = getChannel();
  if (ch) {
    ch.addEventListener('message', (event) => {
      if (event.data?.type === SW_MESSAGE_TYPE) {
        cachedValue = event.data.value === true;
      }
    });
  }
}

export default {
  get,
  getCached,
  set,
  subscribe,
  syncToServiceWorker,
};
